import { BLOCKS } from '@contentful/rich-text-types'
import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
  DEFAULT_MAX_SVG_HEIGHT,
  DEFAULT_MIN_SVG_WIDTH,
} from '@contexts/asset-options'
import { GridOptionsContext } from '@contexts/grid-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { heading1ImageText } from '@render-node/heading-1-image-text'
import { heading2ImageText } from '@render-node/heading-2-image-text'
import { heading3GreenSpanInherit } from '@render-node/heading-3-green-span-inherit'
import { paragraphNoGutter } from '@render-node/paragraph-no-gutter'
import { document } from '@render-node/refresh-document'
import { documentHeading3 } from '@render-node/refresh-document-heading-3'
import { children } from '@render-node/utils/children'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { CtaLink } from '@ui/cta-link'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import clsx from 'clsx'
import { graphql } from 'gatsby'
import React, { useContext } from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: '100%',
  height: 'auto',
  minSvgWidth: DEFAULT_MIN_SVG_WIDTH,
  maxSvgHeight: DEFAULT_MAX_SVG_HEIGHT,
  objectFit: 'contain',
  alt: '',
}

const ContentfulContentArticleRefreshDefault: Utils.EntryFC<
  Queries.ContentfulContentArticleRefreshDefaultFragment
> = ({
  content: {
    contentArticleTitle: title,
    image,
    imageText,
    content,
    contentLinkText,
    contentLinkUrl,
  },
  entryInspectorProps,
  fieldInspectorProps,
}) => {
  const hasFooter = contentLinkText && contentLinkUrl
  const hasContent = title || content || hasFooter
  const gridOptions = useContext(GridOptionsContext)
  const forceRowLayout = gridOptions.col.laptop === 1
  return (
    <div
      className={clsx(
        'mt-0 flex flex-col items-stretch',
        'odd:group-odd/section:laptop:flex-row-reverse',
        'even:group-even/section:laptop:flex-row-reverse',
        forceRowLayout && 'max-w-[560px]',
        forceRowLayout && 'laptop:max-w-none',
        forceRowLayout && 'laptop:flex-row',
        forceRowLayout && 'laptop:justify-stretch',
        image ? 'gap-[40px] laptop:gap-[124px]' : 'gap-0'
      )}
      {...entryInspectorProps()}
    >
      {image && (
        <div
          className={clsx(
            'flex w-full flex-1 self-center laptop:self-start',
            (!title && !content && !hasFooter) || !forceRowLayout
              ? 'basis-auto'
              : 'basis-auto laptop:basis-6/12'
          )}
        >
          <div
            className="flex w-full justify-center"
            {...fieldInspectorProps({
              fieldId: 'image',
            })}
          >
            <AssetOptionsContext.Provider value={assetOptions}>
              <Entry
                content={image}
                mapping={{
                  [ContentType.ContentfulAsset]: ContentfulAsset,
                }}
              />
            </AssetOptionsContext.Provider>
          </div>
        </div>
      )}
      {hasContent && (
        <div
          className={clsx(
            'flex flex-1 flex-col',
            !image || !forceRowLayout
              ? 'basis-auto'
              : 'basis-auto laptop:basis-6/12'
          )}
        >
          <>
            {title && (
              <RichText
                {...fieldInspectorProps({
                  fieldId: 'title',
                })}
                content={title}
                options={{
                  ...defaultRichTextFieldOptionsDeprecated,
                  renderNode: {
                    ...defaultRichTextFieldOptionsDeprecated.renderNode,
                    [BLOCKS.DOCUMENT]: documentHeading3,
                    [BLOCKS.HEADING_3]: heading3GreenSpanInherit,
                    [BLOCKS.PARAGRAPH]: children,
                  },
                }}
              />
            )}
            {content && (
              <RichText
                {...fieldInspectorProps({
                  fieldId: 'content',
                })}
                content={content}
                options={{
                  ...defaultRichTextFieldOptionsDeprecated,
                  renderNode: {
                    ...defaultRichTextFieldOptionsDeprecated.renderNode,
                    [BLOCKS.DOCUMENT]: document,
                  },
                }}
              />
            )}
            {imageText && (
              <RichText
                {...fieldInspectorProps({
                  fieldId: 'imageText',
                })}
                content={imageText}
                options={{
                  renderNode: {
                    ...defaultRichTextFieldOptionsDeprecated.renderNode,
                    [BLOCKS.HEADING_1]: heading1ImageText,
                    [BLOCKS.HEADING_2]: heading2ImageText,
                    [BLOCKS.PARAGRAPH]: paragraphNoGutter,
                  },
                }}
              />
            )}
            {hasFooter && (
              <div className="mt-9 flex justify-center tablet:justify-start">
                <CtaLink
                  data-role={ENHANCED_COMPONENT_ROLE.SECONDARY}
                  data-type={ENHANCED_COMPONENT_TYPE.CTA}
                  href={contentLinkUrl}
                  {...fieldInspectorProps({
                    fieldId: 'contentLinkText',
                  })}
                >
                  {contentLinkText}
                </CtaLink>
              </div>
            )}
          </>
        </div>
      )}
    </div>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleRefreshDefault on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    imageText {
      ...ContentfulContentArticleImageText
    }
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleRefreshDefault,
  'ContentfulContentArticleRefreshDefault'
)
